// src/components/AddScheduleDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    Typography,
    IconButton,
    DialogContentText,
    Alert
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { createSchedule, updateSchedule } from '../api/competitionApi';

export default function AddScheduleDialog({ open, onClose, competitionId, schedule }) {
    const [formData, setFormData] = useState({
        name: '',
        date: ''
    });
    const [loading, setLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (schedule) {
            setFormData({
                name: schedule.name || '',
                date: schedule.date ? schedule.date.split('T')[0] : ''
            });
        } else {
            setFormData({ name: '', date: '' });
        }
    }, [schedule, open]);

    const handleSubmit = async () => {
        if (!formData.name || !formData.date) {
            setAlertMessage('Please fill in all required fields');
            setAlertOpen(true);
            return;
        }

        try {
            setLoading(true);
            setErrorMessage('');
            if (schedule) {
                await updateSchedule(schedule.id, formData);
            } else {
                await createSchedule(competitionId, formData);
            }
            onClose();
        } catch (error) {
            console.error('Error saving schedule:', error);
            setErrorMessage(error.response?.data?.error || 'Failed to save schedule');
        } finally {
            setLoading(false);
        }
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
        setAlertMessage('');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {schedule ? 'Edit Schedule' : 'Add Schedule'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage('')}>
                        {errorMessage}
                    </Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Schedule Name *"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Saturday Morning, Day 1"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Date *"
                            value={formData.date}
                            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>

            {/* Alert Dialog */}
            <Dialog
                open={alertOpen}
                onClose={handleAlertClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxWidth: { xs: '90vw', sm: '500px' }
                    }
                }}
            >
                <DialogTitle
                    id="alert-dialog-title"
                    sx={{
                        fontWeight: 'medium',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                >
                    Warning
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        id="alert-dialog-description"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                    >
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
                    <Button
                        onClick={handleAlertClose}
                        color="primary"
                        variant="contained"
                        autoFocus
                        sx={{
                            borderRadius: 20,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}